import { altitudeToColor } from '@/lib/aircraft/altitudeColor';
import { SERVICE_COLOR } from '@/lib/map/layers/surveillance';
import type { DsBadge, MapObject } from '../types';

/** Marker palette (mirrors the mockup's DS_MAP_COLOURS). */
export const DS_MAP_COLOURS: Record<DsBadge | 'aircraft' | 'waypoint' | 'default', string> = {
  telemetry: '#22c55e',
  surveillance: '#2563eb',
  traffic: '#f59e0b',
  system: '#94a3b8',
  aircraft: '#a855f7',
  waypoint: '#f97316',
  default: '#64748b',
};

function asRecord(v: unknown): Record<string, unknown> | null {
  return v !== null && typeof v === 'object' ? (v as Record<string, unknown>) : null;
}
const num = (v: unknown): number | null => (typeof v === 'number' ? v : null);
const str = (v: unknown): string | null => (typeof v === 'string' ? v : null);

function valid(lat: unknown, lng: unknown): boolean {
  return (
    typeof lat === 'number' &&
    typeof lng === 'number' &&
    Math.abs(lat) <= 90 &&
    Math.abs(lng) <= 180 &&
    !(lat === 0 && lng === 0)
  );
}

const pickAlt = (...vals: Array<number | null>): number | undefined => {
  for (const v of vals) if (v != null && Number.isFinite(v)) return v;
  return undefined;
};

/** Normalize an MQTT payload into map objects. See spec §4 for the typing table. */
export function extractMapObjects(payload: unknown, badge: DsBadge, topic: string): MapObject[] {
  const root = asRecord(payload);
  if (!root) return [];
  const badgeColour = DS_MAP_COLOURS[badge] ?? DS_MAP_COLOURS.default;
  const tail = topic.split('/').pop() ?? 'point';

  // 1. aircraft[] → aircraft kind
  if (Array.isArray(root.aircraft)) {
    const out: MapObject[] = [];
    for (const item of root.aircraft) {
      const a = asRecord(item);
      if (!a || !valid(a.lat, a.lon)) continue;
      const label = (str(a.flight)?.trim() || str(a.hex)) ?? 'aircraft';
      const altitudeFt = pickAlt(num(a.alt_geom), num(a.alt_baro));
      out.push({
        id: (str(a.hex) || str(a.flight)?.trim()) ?? label,
        lat: a.lat as number,
        lng: a.lon as number,
        kind: 'aircraft',
        heading: num(a.track) ?? num(a.nav_heading) ?? undefined,
        altitudeFt,
        label,
        colour: altitudeToColor(altitudeFt ?? null),
        fields: {
          flight: str(a.flight)?.trim(),
          hex: str(a.hex),
          gs: num(a.gs),
          squawk: str(a.squawk),
          category: str(a.category),
          emitter: tail,
          altitudeFt,
        },
      });
    }
    return out;
  }

  // 2. devices[] → dot kind
  if (Array.isArray(root.devices)) {
    const out: MapObject[] = [];
    for (const item of root.devices) {
      const d = asRecord(item);
      if (!d || !valid(d.lat, d.lon)) continue;
      const label = str(d.id) ?? 'device';
      out.push({
        id: label,
        lat: d.lat as number,
        lng: d.lon as number,
        kind: 'dot',
        label,
        colour: SERVICE_COLOR[tail] ?? badgeColour,
        fields: { model: str(d.model), service: tail },
      });
    }
    return out;
  }

  // 3. waypoints[] → dot kind (orange)
  if (Array.isArray(root.waypoints)) {
    const out: MapObject[] = [];
    root.waypoints.forEach((wp, i) => {
      if (!Array.isArray(wp) || wp.length < 2) return;
      const [lat, lng] = wp;
      if (!valid(lat, lng)) return;
      out.push({
        id: `WP${i + 1}`,
        lat,
        lng,
        kind: 'dot',
        label: `WP${i + 1}`,
        colour: DS_MAP_COLOURS.waypoint,
        fields: {},
      });
    });
    return out;
  }

  // 4. single point — drone telemetry vs generic dot
  const inner = asRecord(root.payload);
  const header = asRecord(root.header);
  const serial = str(header?.aircraft_serial) ?? str(root.drone_id) ?? str(root.aircraft_serial);
  const latSrc = valid(root.lat, root.lon)
    ? { lat: root.lat as number, lng: root.lon as number }
    : inner && valid(inner.latitude, inner.longitude)
      ? { lat: inner.latitude as number, lng: inner.longitude as number }
      : valid(root.latitude, root.longitude)
        ? { lat: root.latitude as number, lng: root.longitude as number }
        : null;
  if (!latSrc) return [];

  const heading = num(inner?.heading) ?? num(root.heading) ?? undefined;
  const isDrone =
    topic.startsWith('flysafe/v1') ||
    topic.startsWith('networkid') ||
    // v1/output namespace (ticket 86e2jbe3d) — `networkid` and `telemetry`
    // both carry a position for the aircraft named in the topic.
    topic.startsWith('v1/output/aircraft/') ||
    (serial != null && heading != null);

  if (isDrone) {
    const altitudeFt = pickAlt(
      num(inner?.geodetic_altitude),
      num(inner?.altitude_msl),
      num(root.geodetic_altitude),
      num(root.pressure_altitude),
    );
    const label = serial ?? tail;
    return [
      {
        id: serial ?? tail,
        ...latSrc,
        kind: 'drone',
        heading,
        altitudeFt,
        label,
        colour: DS_MAP_COLOURS.telemetry,
        fields: {
          serial,
          altitudeFt,
          heading,
          ground_speed: num(inner?.ground_speed) ?? num(root.ground_speed),
        },
      },
    ];
  }
  return [{ id: tail, ...latSrc, kind: 'dot', label: tail, colour: badgeColour, fields: {} }];
}
